import { avatarAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

const avatarSlotRecipe = defineSlotRecipe({
  slots: [...avatarAnatomy.keys(), 'icon', 'badge', 'badgeContainer'],
  className: 'avatar',
  base: {
    root: {
      alignItems: 'center',
      border: '1px solid',
      borderColor: 'border/minimal',
      display: 'inline-flex',
      flexShrink: '0',
      justifyContent: 'center',
      position: 'relative',
      userSelect: 'none',
      verticalAlign: 'top',
      '&:has([data-part=image][data-state=visible])': {
        backgroundColor: 'transparent',
      },
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    fallback: {},
    icon: {},
    badge: {
      width: 16,
      height: 16,
    },
    badgeContainer: {
      display: 'flex',
      boxShadow: 'elevation/sm',
      position: 'absolute',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text/secondary',
      backgroundColor: 'background/primary',
    },
  },

  variants: {
    size: {
      '24': {
        root: { width: 24, height: 24, borderRadius: 4 },
        image: { borderRadius: 'calc({radii.4} - 1px)' },
        fallback: { textStyle: 'comp/avatar/24', borderRadius: 'calc({radii.4} - 1px)' },
        icon: { width: 16, height: 16 },
        badgeContainer: { display: 'none' },
      },
      '32': {
        root: { width: 32, height: 32, borderRadius: 6 },
        image: { borderRadius: 'calc({radii.6} - 1px)' },
        fallback: { textStyle: 'comp/avatar/32', borderRadius: 'calc({radii.6} - 1px)' },
        icon: { width: 20, height: 20 },
        badgeContainer: { width: 16, height: 16, bottom: -5, right: -5, borderRadius: 4 },
      },
      '40': {
        root: { width: 40, height: 40, borderRadius: 8 },
        image: { borderRadius: 'calc({radii.8} - 1px)' },
        fallback: { textStyle: 'comp/avatar/40', borderRadius: 'calc({radii.8} - 1px)' },
        icon: { width: 24, height: 24 },
        badgeContainer: { width: 16, height: 16, bottom: -2, right: -2, borderRadius: 4 },
      },
      '48': {
        root: { width: 48, height: 48, borderRadius: '10' },
        image: { borderRadius: 'calc({radii.10} - 1px)' },
        fallback: { textStyle: 'comp/avatar/48', borderRadius: 'calc({radii.10} - 1px)' },
        icon: { width: 32, height: 32 },
        badgeContainer: { width: 24, height: 24, bottom: -7, right: -7, borderRadius: '50%' },
      },
      '64': {
        root: { width: 64, height: 64, borderRadius: 12 },
        image: { borderRadius: 'calc({radii.12} - 1px)' },
        fallback: { textStyle: 'comp/avatar/64', borderRadius: 'calc({radii.12} - 1px)' },
        icon: { width: 48, height: 48 },
        badgeContainer: { width: 24, height: 24, bottom: -5, right: -5, borderRadius: '50%' },
      },
      '128': {
        root: { width: 128, height: 128, borderRadius: 16 },
        image: { borderRadius: 'calc({radii.16} - 1px)' },
        fallback: { textStyle: 'comp/avatar/128', borderRadius: 'calc({radii.16} - 1px)' },
        icon: { width: 96, height: 96 },
        badgeContainer: { display: 'none' },
      },
    },

    variant: {
      circle: {
        root: { borderRadius: '50%' },
        image: { borderRadius: '50%' },
        fallback: { borderRadius: '50%' },
      },
      rounded: {},
    },

    colorVariant: {
      neutral: { root: { color: 'color/neutral/strong', backgroundColor: 'color/neutral/subtle' } },
      purple: { root: { color: 'color/purple/strong', backgroundColor: 'color/purple/subtle' } },
      indigo: { root: { color: 'color/indigo/strong', backgroundColor: 'color/indigo/subtle' } },
      blue: { root: { color: 'color/blue/strong', backgroundColor: 'color/blue/subtle' } },
      green: { root: { color: 'color/green/strong', backgroundColor: 'color/green/subtle' } },
      yellow: { root: { color: 'color/yellow/strong', backgroundColor: 'color/yellow/subtle' } },
      red: { root: { color: 'color/red/strong', backgroundColor: 'color/red/subtle' } },
      orange: { root: { color: 'color/orange/strong', backgroundColor: 'color/orange/subtle' } },
      turquoise: { root: { color: 'color/turquoise/strong', backgroundColor: 'color/turquoise/subtle' } },
    },
  },

  defaultVariants: {
    colorVariant: 'neutral',
    size: '32',
    variant: 'circle',
  },
});

export default avatarSlotRecipe;
